import { useNavigation } from '@refinedev/core';
import * as Sentry from '@sentry/react';
import { useEffect, useState } from 'react';
import {
	createBrowserRouter,
	Link,
	Navigate,
	RouterProvider,
	useLocation,
} from 'react-router-dom';
import Admin from './admin';
import { NavigationLoader } from './components/NavigationLoader';
import { PageTransition } from './components/PageTransition';
import { ToastProvider } from './contexts/ToastContext';
import './index.css';

function Marquee() {
	const marqueeText =
		"Available for hire — Currently accepting new projects — Let's create something amazing together";

	return (
		<div className='fixed top-[100px] left-0 right-0 h-[24px] bg-accent overflow-hidden flex items-center shadow-md z-10'>
			<div className='flex whitespace-nowrap animate-scroll'>
				{/* First set of content */}
				<div className='flex items-center shrink-0'>
					<span className='marquee-text'>
						{marqueeText} — {marqueeText} — {marqueeText} —&nbsp;
					</span>
				</div>
				{/* Duplicate set for seamless loop */}
				<div className='flex items-center shrink-0'>
					<span className='marquee-text'>
						{marqueeText} — {marqueeText} — {marqueeText} —&nbsp;
					</span>
				</div>
			</div>
		</div>
	);
}

function NavBar() {
	const location = useLocation();
	const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

	// Close mobile menu when route changes
	useEffect(() => {
		setMobileMenuOpen(false);
	}, [location.pathname]);

	// Close mobile menu when clicking outside
	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (
				mobileMenuOpen &&
				!(event.target as Element).closest('.mobile-nav-menu')
			) {
				setMobileMenuOpen(false);
			}
		};

		if (mobileMenuOpen) {
			document.addEventListener('mousedown', handleClickOutside);
			return () =>
				document.removeEventListener('mousedown', handleClickOutside);
		}
	}, [mobileMenuOpen]);

	return (
		<>
			<nav className='fixed top-0 left-0 right-0 z-10 border-b border-muted/10 bg-background/80 backdrop-blur-sm'>
				<div className='flex items-center justify-between h-[100px] px-4'>
					<Link
						to='/'
						aria-label='Home'
						className='inline-flex items-center hover:scale-110 hover:-rotate-3 transition-transform'>
						<img
							src='/images/wtlogo2.png'
							alt='WT Logo'
							className='max-h-20 h-auto w-auto object-contain block'
						/>
					</Link>

					{/* Desktop Navigation */}
					<div className='hidden md:flex space-x-6 items-center'>
						<Link
							to='/portfolio'
							className={`nav-link text-[1.125rem] uppercase ${
								location.pathname === '/portfolio' ? 'active' : ''
							}`}>
							Portfolio
						</Link>
						<Link
							to='/projects'
							className={`nav-link text-[1.125rem] uppercase ${
								location.pathname === '/projects' ? 'active' : ''
							}`}>
							Projects
						</Link>
						<Link
							to='/wiggleworm'
							className={`nav-link text-[1.125rem] uppercase ${
								location.pathname === '/wiggleworm' ? 'active' : ''
							}`}>
							Wiggleworm
						</Link>
						<Link
							to='/contact'
							className={`nav-link text-[1.125rem] uppercase ${
								location.pathname === '/contact' ? 'active' : ''
							}`}>
							Contact
						</Link>
					</div>

					{/* Mobile Menu Button */}
					<button
						onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
						className='md:hidden p-2 rounded-lg hover:bg-muted/10 transition-colors'
						aria-label='Toggle mobile menu'
						aria-expanded={mobileMenuOpen}>
						<svg
							className='w-6 h-6 text-text'
							fill='none'
							stroke='currentColor'
							viewBox='0 0 24 24'>
							{mobileMenuOpen ? (
								<path
									strokeLinecap='round'
									strokeLinejoin='round'
									strokeWidth={2}
									d='M6 18L18 6M6 6l12 12'
								/>
							) : (
								<path
									strokeLinecap='round'
									strokeLinejoin='round'
									strokeWidth={2}
									d='M4 6h16M4 12h16M4 18h16'
								/>
							)}
						</svg>
					</button>
				</div>
			</nav>

			{/* Mobile Menu Overlay */}
			{mobileMenuOpen && (
				<div
					className='mobile-nav-menu fixed inset-0 z-50 md:hidden'
					role='dialog'
					aria-modal='true'>
					{/* Backdrop */}
					<div className='fixed inset-0 bg-black/50 backdrop-blur-sm' />

					{/* Menu Panel */}
					<div className='fixed top-0 right-0 h-full w-64 bg-background border-l border-muted/20 shadow-xl'>
						{/* Header */}
						<div className='flex items-center justify-between p-4 border-b border-muted/10'>
							<span className='text-lg font-semibold text-text'>Menu</span>
							<button
								onClick={() => setMobileMenuOpen(false)}
								className='p-2 rounded-lg hover:bg-muted/10 transition-colors'
								aria-label='Close mobile menu'>
								<svg
									className='w-5 h-5 text-text'
									fill='none'
									stroke='currentColor'
									viewBox='0 0 24 24'>
									<path
										strokeLinecap='round'
										strokeLinejoin='round'
										strokeWidth={2}
										d='M6 18L18 6M6 6l12 12'
									/>
								</svg>
							</button>
						</div>

						{/* Navigation Links */}
						<nav className='p-4 space-y-2'>
							<Link
								to='/portfolio'
								className={`block py-3 px-4 rounded-lg text-lg uppercase transition-colors ${
									location.pathname === '/portfolio'
										? 'bg-accent/10 text-accent font-semibold'
										: 'text-text hover:bg-muted/10'
								}`}>
								Portfolio
							</Link>
							<Link
								to='/projects'
								className={`block py-3 px-4 rounded-lg text-lg uppercase transition-colors ${
									location.pathname === '/projects'
										? 'bg-accent/10 text-accent font-semibold'
										: 'text-text hover:bg-muted/10'
								}`}>
								Projects
							</Link>
							<Link
								to='/wiggleworm'
								className={`block py-3 px-4 rounded-lg text-lg uppercase transition-colors ${
									location.pathname === '/wiggleworm'
										? 'bg-accent/10 text-accent font-semibold'
										: 'text-text hover:bg-muted/10'
								}`}>
								Wiggleworm
							</Link>
							<Link
								to='/contact'
								className={`block py-3 px-4 rounded-lg text-lg uppercase transition-colors ${
									location.pathname === '/contact'
										? 'bg-accent/10 text-accent font-semibold'
										: 'text-text hover:bg-muted/10'
								}`}>
								Contact
							</Link>
						</nav>
					</div>
				</div>
			)}
		</>
	);
}

function MainSiteLayout() {
	const location = useLocation();
	const isWigglewormStyle =
		location.pathname === '/wiggleworm' || location.pathname === '/';
	return (
		<ToastProvider>
			<div
				className={`min-h-screen flex flex-col ${
					isWigglewormStyle ? 'bg-transparent' : 'bg-background'
				} text-text`}>
				{!isWigglewormStyle && <div className='noise'></div>}
				{!isWigglewormStyle && <NavigationLoader />}
				{!isWigglewormStyle && <NavBar />}
				{!isWigglewormStyle && <Marquee />}
				<div
					className={`flex-1 relative z-[1] ${
						isWigglewormStyle ? '' : 'pt-[124px]'
					} ${isWigglewormStyle ? 'bg-transparent' : 'bg-background'}`}>
					<PageTransition />
				</div>
			</div>
		</ToastProvider>
	);
}

const router = createBrowserRouter(
	[
		{ path: '/admin/*', element: <Admin /> },

		{ path: '/*', element: <MainSiteLayout /> },
	],
	{ future: { v7_relativeSplatPath: true } }
);

function App() {
	return (
		<Sentry.ErrorBoundary
			fallback={({ error }) => (
				<div className='min-h-screen bg-gray-50 flex items-center justify-center p-4'>
					<div className='max-w-md w-full bg-white rounded-lg shadow-lg p-8'>
						<div className='flex items-center justify-center w-16 h-16 bg-red-100 rounded-full mx-auto mb-4'>
							<svg
								className='w-8 h-8 text-red-600'
								fill='none'
								viewBox='0 0 24 24'
								stroke='currentColor'>
								<path
									strokeLinecap='round'
									strokeLinejoin='round'
									strokeWidth={2}
									d='M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z'
								/>
							</svg>
						</div>
						<h1 className='text-2xl font-bold text-gray-900 text-center mb-2'>
							Something Went Wrong
						</h1>
						<p className='text-gray-600 text-center mb-6'>
							We're sorry, but an unexpected error occurred. The error has been
							logged and we'll investigate it.
						</p>
						{import.meta.env.DEV && (
							<div className='bg-red-50 border border-red-200 rounded p-4 mb-6'>
								<p className='text-sm text-red-800 font-mono break-all'>
									{error instanceof Error ? error.message : String(error)}
								</p>
							</div>
						)}
						<div className='flex gap-3'>
							<button
								onClick={() => window.location.reload()}
								className='flex-1 bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 transition'>
								Reload Page
							</button>
							<button
								onClick={() => (window.location.href = '/')}
								className='flex-1 bg-gray-200 text-gray-700 px-4 py-2 rounded hover:bg-gray-300 transition'>
								Go Home
							</button>
						</div>
					</div>
				</div>
			)}
			showDialog={false}>
			<RouterProvider router={router} />
		</Sentry.ErrorBoundary>
	);
}

export const CatchAllNavigate = ({ to }: { to: string }) => {
	return <Navigate to={to} />;
};

export const NavigateToResource = ({ resource }: { resource: string }) => {
	const { list } = useNavigation();
	list(resource);
	return null;
};

export default App;
